import React, { useCallback, useEffect, useRef, useState } from 'react';
import { inkButtonSx } from '../../../../get-started/inkButtonSx';
import { Box, Button, ButtonBase, Chip, CircularProgress, Stack, Typography, alpha } from '@mui/material';
import { TOUR_ANCHOR } from '../../../../welcome-tour/tourAnchors';
import { ArrowLeftOutlined, CheckCircleFilled, CreditCardOutlined, LockOutlined, PlusOutlined } from '@ant-design/icons';
import useAuth from 'hooks/useAuth';
import { useBillingClient } from 'client/client-hook';
import { useDispatch } from 'store';
import { DispatchSnackbarService } from 'services';
import { PAYMENT_TAB, useAmountDueNow, useCheckoutActions, useCheckoutState, useCheckoutStore, type PaymentTab } from '../store';
import { trackPurchase } from 'utils/gtm';
import { useQuickSubscribe } from 'sections/get-started/useQuickSubscribe';
import { useBillingStore } from '../store/useBillingStore';
import { OVERDUE_RECHECK, isOverdueUnpayable, overdueCryptoTopUpCents, recheckOverdueInvoice } from '../store/overdue';
import { CHECKOUT_COPY, executeCheckoutPlan, planHasChanges } from 'lib/billing';
import { buildCheckoutPlan } from './checkoutPlan';
import { ListCardsReply, ListCardsReq, RenewOverDueSubscriptionReq } from 'proto/billing-manager_pb';
import { TotalSummary } from '../Configure/TotalSummary';
import { PillTabs, PillTabsOption } from '../PillTabs';
import { StepHeader } from '../StepHeader';
import { formatCents } from '../format';
import { StripeCardElement, StripeCardHandle } from './StripeCardElement';
import { getUserStripe } from 'lib/stripe/userStripe';
import { CryptoElement, CryptoHandle } from './CryptoElement';
import { OverdueCryptoPanel } from './OverdueCryptoPanel';
import { OverdueAmountSummary } from './OverdueAmountSummary';
import { makeGrpcAdapter } from './checkoutExecutorAdapter';
import { trackSubscriptionCreated } from 'utils/oaiq';

type SavedCard = ListCardsReply.AsObject['cardsList'][number];

const service = new DispatchSnackbarService();

const PAYMENT_TABS: PillTabsOption<PaymentTab>[] = [
  { id: PAYMENT_TAB.CARD, label: CHECKOUT_COPY.payment.tabs.card },
  { id: PAYMENT_TAB.CRYPTO, label: CHECKOUT_COPY.payment.tabs.crypto }
];

function brandLabel(brand?: string) {
  if (!brand) return 'Card';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

type SavedRowProps = { selected: boolean; onClick: () => void; children: React.ReactNode };
function SelectableRow({ selected, onClick, children }: SavedRowProps) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={(theme) => ({
        width: '100%',
        justifyContent: 'flex-start',
        gap: 1.5,
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
        transition: 'border-color 150ms ease, background 150ms ease',
        '&:hover': selected ? {} : { borderColor: 'primary.light' }
      })}
    >
      {children}
    </ButtonBase>
  );
}

function SavedCardsList({
  cards,
  selectedId,
  useNewCard,
  onSelectCard,
  onUseNewCard
}: {
  cards: SavedCard[];
  selectedId: string | null;
  useNewCard: boolean;
  onSelectCard: (id: string) => void;
  onUseNewCard: () => void;
}) {
  return (
    <Stack spacing={1}>
      {cards.map((c) => {
        const selected = !useNewCard && c.id === selectedId;
        return (
          <SelectableRow key={c.id} selected={selected} onClick={() => onSelectCard(c.id)}>
            <CreditCardOutlined style={{ fontSize: 18, color: selected ? undefined : 'currentColor' }} />
            <Stack spacing={0.1} sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="body2" fontWeight={600}>
                  {brandLabel(c.brand)} •••• {c.cardNumber}
                </Typography>
                {c.isDefault?.value && (
                  <Chip
                    size="small"
                    label="Default"
                    sx={{
                      height: 16,
                      fontSize: 9,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      '& .MuiChip-label': { px: 0.625, fontWeight: 700 }
                    }}
                  />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Expires {String(c.expiryMonth).padStart(2, '0')}/{c.expiryYear}
              </Typography>
            </Stack>
            {selected && <CheckCircleFilled style={{ fontSize: 16 }} />}
          </SelectableRow>
        );
      })}
      <SelectableRow selected={useNewCard} onClick={onUseNewCard}>
        <PlusOutlined style={{ fontSize: 14 }} />
        <Typography variant="body2" fontWeight={600}>
          Use a new card
        </Typography>
      </SelectableRow>
    </Stack>
  );
}

export function PaymentStep() {
  const COPY = CHECKOUT_COPY.payment;
  const state = useCheckoutState();
  const actions = useCheckoutActions();
  // Discount code typed in TotalSummary — sent on create ops only (the
  // executor never attaches it to adds/modifies; trial buckets carry it too,
  // though the backend skips the discount on trials).
  const coupon = useCheckoutStore((s) => s.coupon);
  const { user } = useAuth() as any;
  const getBillingClient = useBillingClient();
  const dispatch = useDispatch();

  // Crypto is a one-time, charge-now rail — it can't settle a $0 invoice (the
  // provider rejects "nothing to transfer"). Two $0 cases, handled differently:
  //
  //  • Trial — the charge is deferred to trial-end. Hide crypto and keep the
  //    card required, so the sub actually converts once the trial lapses.
  //  • Credit covers the due (non-trial) — keep crypto VISIBLE; the buyer may
  //    still pick it, but we settle off the account balance instead of
  //    redirecting to the provider. The card form likewise becomes optional
  //    (kept only so they can save one for renewal). See handleCryptoSubmit /
  //    handleCardSubmit.
  const { dueCents, trialApplies, discountedPreviewApplies } = useAmountDueNow();
  // "Credit covers it" must mean actual account credit. On the new-user
  // discounted-preview path a 100%-off code can zero dueCents — treating that
  // as credit-covered would skip confirmSetup and create the subscription
  // with no payment method for the post-coupon renewal, so a coupon-driven
  // $0 ALWAYS collects the card (a once-coupon's renewal bills full price).
  const creditCoversDue = dueCents <= 0 && !trialApplies && !discountedPreviewApplies;
  // Invoice-pay mode: settling a specific overdue invoice, not a plan. Crypto is
  // always offered here (balance or fresh top-up), and the plan-driven $0/trial
  // hide rules below don't apply.
  const overdueInvoice = state.overdueInvoice;
  const availableCredit = Number((state as any).billingOverview?.availableCredit || 0);
  // Crypto has nothing to settle on a $0 order (the provider rejects it):
  // hidden on trials, and on a coupon-driven $0 due (100%-off code).
  const hideCrypto = !overdueInvoice && (trialApplies || (discountedPreviewApplies && dueCents <= 0));
  const paymentTabs = hideCrypto ? PAYMENT_TABS.filter((t) => t.id !== PAYMENT_TAB.CRYPTO) : PAYMENT_TABS;

  // Store-held: the order summary re-quotes without the cancel offer while crypto is chosen.
  const tab = useCheckoutStore((s) => s.paymentTab);
  const setTab = useCheckoutStore((s) => s.setPaymentTab);
  const [submitting, setSubmitting] = useState(false);
  const [stripeReady, setStripeReady] = useState(false);
  // Saved cards picker. When the user has cards we default to charging the
  // selected one and skip confirmSetup (no fresh PaymentMethod attached, which
  // used to bloat the Payment Methods list). `useNewCard` flips to the Stripe
  // form; confirmSetup only runs when the user actually enters a new card.
  const [cards, setCards] = useState<SavedCard[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [cardEntered, setCardEntered] = useState(false);
  const stripeRef = useRef<StripeCardHandle>(null);
  const cryptoRef = useRef<CryptoHandle>(null);

  const showError = useCallback(
    (msg: string) => {
      service.dispatchSnackbar(dispatch, msg, 'alert', 'error', 5000);
    },
    [dispatch]
  );

  // Force-refetch BillingOverview on PaymentStep mount so any credit balance
  // added/changed mid-flow (e.g. user topped up Stripe balance between Configure
  // and Payment, or a refund landed) is reflected in the totals before charging.
  // Cached overview from initial drawer open would otherwise be stale.
  const fetchBilling = useBillingStore((s) => s.fetch);
  useEffect(() => {
    if (!user?.tcId) return;
    fetchBilling(user.tcId, { force: true });
  }, [user?.tcId, fetchBilling]);

  useEffect(() => {
    let cancelled = false;
    if (!user?.tcId) return;
    setCardsLoading(true);
    (async () => {
      try {
        const client = await getBillingClient();
        const req: ListCardsReq.AsObject = { userId: user.tcId };
        const resp = (await client.listCards(req)) as ListCardsReply.AsObject;
        if (cancelled) return;
        const list = resp?.cardsList || [];
        setCards(list);
        const def = list.find((c) => c.isDefault?.value) || list[0] || null;
        setSelectedCardId(def?.id || null);
        // No saved cards on file → drop straight to the new-card form.
        setUseNewCard(list.length === 0);
      } catch {
        if (!cancelled) {
          setCards([]);
          setSelectedCardId(null);
          setUseNewCard(true);
        }
      } finally {
        if (!cancelled) setCardsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.tcId, getBillingClient]);

  // If crypto becomes unavailable (trial turns on, or credit/preview drops the
  // amount due to $0) while its tab is active, snap back to card so the buyer
  // never submits via a hidden rail.
  useEffect(() => {
    if (hideCrypto && tab === PAYMENT_TAB.CRYPTO) setTab(PAYMENT_TAB.CARD);
  }, [hideCrypto, tab, setTab]);

  const usingSaved = !useNewCard && !!selectedCardId;

  // Builds and runs the checkout plan, then confirms the new sub's first-invoice
  // PaymentIntent when one was issued. Shared by every settle path — saved card,
  // new card, and the credit-only $0 case (payViaCredit). Only the optional card
  // token and the saved-vs-new confirm differ; a $0 due settles off the account
  // balance and surfaces no client secret, so this returns with no Stripe confirm.
  async function executeCheckout(client: Awaited<ReturnType<typeof getBillingClient>>, pmId: string | undefined, usingSaved: boolean) {
    const subscriptions = state.subscriptions || [];

    // Same plan resolution the crypto tab uses (buildCheckoutPlan) so both settle identically.
    const { plan, trialContext } = buildCheckoutPlan(state);

    // A plan with no actionable op must never read as a purchase. executeCheckoutPlan
    // short-circuits a noop — it returns `{status:'unchanged'}` with NO billingState
    // and makes zero API calls — while the success path below keys only off the
    // absence of a client secret (which a $0-due checkout legitimately has). Without
    // this guard the user gets "Subscription updated" and onComplete({status:'success'})
    // for a purchase that never happened. Reachable when the staged cart is wiped by a
    // drawer re-hydrate (close + reopen), leaving the selection equal to current state.
    if (!planHasChanges(plan)) {
      showError(CHECKOUT_COPY.payment.noChanges);
      return;
    }

    const grpc = makeGrpcAdapter(client, user.tcId);
    const result: any = await executeCheckoutPlan({
      plan,
      body: { tolt_referral: (window as any).tolt_referral || '', coupon },
      subscriptions,
      catalog: state.catalog,
      grpc,
      trialContext
    });

    // Defence in depth: only the real path builds a billingState. If the executor
    // returned without one, nothing was submitted — do not claim success.
    if (!result?.billingState) {
      showError(CHECKOUT_COPY.payment.noChanges);
      return;
    }

    // A brand-new subscription (e.g. a Dedicated Environment) is created with
    // payment_behavior=default_incomplete, so its first-invoice PaymentIntent
    // must be confirmed — even on a saved card. Confirm whenever the executor
    // surfaced a client secret (a $0 due settles off credit and surfaces none).
    const piSecret = result?.billingState?.lastClientSecret;
    if (piSecret) {
      if (usingSaved) {
        // No mounted PaymentElement on the saved-card path → confirm with a
        // standalone Stripe instance, naming the chosen card: a PI whose first
        // try failed no longer carries one.
        const stripe = await getUserStripe(user.tcId, getBillingClient);
        const savedPmId = cards.find((c) => c.id === selectedCardId)?.paymentMethodId;
        const res: any = await stripe?.confirmCardPayment(piSecret, savedPmId ? { payment_method: savedPmId } : undefined);
        const okStatus = res?.paymentIntent?.status === 'succeeded' || res?.paymentIntent?.status === 'processing';
        if (!res || res.error || !okStatus) {
          showError(res?.error?.message || CHECKOUT_COPY.payment.card.confirmFailed);
          return;
        }
      } else {
        // New-card PI has no attached PM (no customer default) → pass the PM
        // confirmSetup just created so Stripe can charge it.
        const ok = await stripeRef.current?.confirmPaymentIntent(piSecret, pmId || undefined);
        if (!ok) return;
      }
    }

    // OpenAI ChatGPT Ads conversion — exactly one event per checkout.
    //
    // Keyed on checkout_ref rather than a subscription id, because no single
    // subscription represents this purchase: executeCheckoutPlan splits a cart
    // across several Stripe subscriptions (trial-eligible items always get
    // their own), and `billingState.subscriptionId` is last-write-wins across
    // modifies/adds/creates/removes — after a cancellation it holds an EXISTING
    // subscription whose id was already consumed by its own original purchase.
    // checkout_ref already rides every create to billing-manager, so the Phase 2
    // server event can resolve the same id.
    //
    // Gated on subscriptions actually created: a pure modify (seat change, plan
    // upgrade) is an upsell and belongs to the server-side invoice-keyed lane,
    // and a create that came back ALREADY_EXISTS created nothing at all.
    // A pure trial reports NOTHING: `trial_started` has no conversion-event
    // setting in Ads Manager, so OpenAI would receive and discard it. Decided
    // from what was actually created, not from the cart-wide `trialApplies`
    // flag — that one is true as soon as ANY created sub is trial-eligible, so
    // a mixed trial+paid cart would wrongly suppress real revenue.
    const created: { id: string; isTrial: boolean }[] = result?.createdSubscriptions ?? [];
    if (created.length > 0 && !created.every((c) => c.isTrial)) {
      trackSubscriptionCreated({
        checkout_ref: result?.checkoutRef,
        // dueCents is what is charged TODAY across the whole cart (net of
        // credit, proration and discount); oaiq/gtm take major units.
        value: (dueCents ?? 0) / 100
      });
    }

    // GTM + PostHog conversion for this entire funnel. Every LEGACY checkout
    // path fires trackPurchase; checkout-v2 — the path the locked dashboard and
    // the quick-subscribe modal both use — never did, so paid conversions from
    // the get-started work were invisible in PostHog and in the experiment
    // results. The OpenAI pixel is already reported above under its own key.
    trackPurchase({
      transaction_id: result?.billingState?.subscriptionId || '',
      value: Math.max(0, dueCents) / 100,
      currency: 'USD',
      checkout_entry: useQuickSubscribe.getState().lastEntry ?? 'checkout_v2',
      skipOaiq: true
    });
    service.dispatchSnackbar(dispatch, CHECKOUT_COPY.payment.successMessage, 'alert', 'success', 4000);
    // Lets the page that launched the drawer tell a paid close from a cancel.
    useCheckoutStore.getState().markCompleted();
    actions.onComplete?.({
      subscriptionId: result?.billingState?.subscriptionId,
      status: 'success',
      userId: user.tcId
    });
  }

  // Nothing left to pay (sub cancelled, invoice settled or voided): close the
  // drawer; the billing page refreshes when it closes.
  function endUnpayableOverdue() {
    showError(COPY.overdue.noLongerPayable);
    useCheckoutStore.getState().closeDrawer();
  }

  // The drawer keeps the invoice it opened with, so re-read it before paying —
  // and before any card is attached or made default.
  async function overdueInvoiceStillPayable(): Promise<boolean> {
    if (!overdueInvoice) return false;
    const { result, fresh } = await recheckOverdueInvoice(getBillingClient, user.tcId, overdueInvoice);
    if (result === OVERDUE_RECHECK.payable) return true;
    if (result === OVERDUE_RECHECK.unpayable) {
      endUnpayableOverdue();
    } else if (fresh) {
      useCheckoutStore.getState().setOverdueInvoice(fresh);
      showError(COPY.overdue.amountChanged(formatCents(fresh.amountDueCents, fresh.currency)));
    }
    return false;
  }

  // A card just entered becomes the default by its Stripe id, as a saved one does by its card id.
  async function makeEnteredCardDefault(client: Awaited<ReturnType<typeof getBillingClient>>, pmId: string) {
    await client.setDefaultPaymentMethod({ userId: user.tcId, cardId: '', paymentMethodId: pmId });
  }

  // Settle the overdue invoice (invoice-pay mode). pmId omitted → the backend
  // settles it from the credit balance; provided → that card is charged.
  // Handles 3DS/SCA requires_action surfaced by the backend.
  async function executeInvoicePay(client: Awaited<ReturnType<typeof getBillingClient>>, pmId: string | undefined) {
    if (!overdueInvoice) return;
    const req: RenewOverDueSubscriptionReq.AsObject = {
      userId: user.tcId,
      invoiceId: overdueInvoice.invoiceId,
      paymentMethodId: pmId || ''
    };
    let resp: any;
    try {
      resp = await client.renewOverDueSubscription(req);
    } catch (e: any) {
      // The backend refuses an invoice that stopped being payable after the re-check.
      if (!isOverdueUnpayable(e)) throw e;
      endUnpayableOverdue();
      return;
    }
    const pi = resp?.paymentIntent;
    if (pi?.status === 'requires_action' && pi?.clientSecret) {
      const stripe = await getUserStripe(user.tcId, getBillingClient);
      const res: any = await stripe?.confirmCardPayment(pi.clientSecret);
      const ok = res?.paymentIntent?.status === 'succeeded' || res?.paymentIntent?.status === 'processing';
      if (!res || res.error || !ok) {
        showError(res?.error?.message || CHECKOUT_COPY.payment.card.confirmFailed);
        return;
      }
    } else if (pi?.status === 'requires_payment_method') {
      showError(CHECKOUT_COPY.payment.card.confirmFailed);
      return;
    }
    service.dispatchSnackbar(dispatch, CHECKOUT_COPY.payment.successMessage, 'alert', 'success', 4000);
    useCheckoutStore.getState().markCompleted();
    actions.onComplete?.({ status: 'success', userId: user.tcId });
  }

  // Crypto tab in invoice-pay mode. Two sub-paths:
  //  • balance covers the invoice → RenewOverDueSubscription with no payment
  //    method; the backend settles it from the credit balance (Stripe itself
  //    only applies credit when an invoice is finalized, never afterwards).
  //  • balance short → Coingate top-up of what the balance leaves, carrying
  //    payInvoiceId. Unlike the card rail (synchronous — invoice id rides the
  //    RPC and Stripe pays within that request), crypto confirms async long
  //    after this page is gone, so the backend persists payInvoiceId on the
  //    crypto payment row and its cron credits + pays the invoice on webhook.
  async function handleCryptoInvoiceSubmit() {
    if (!user?.tcId || !overdueInvoice) return;
    setSubmitting(true);
    actions.setSubmitting(true);
    try {
      const client = await getBillingClient();
      if (!(await overdueInvoiceStillPayable())) return;
      // Credit may have been spent since the drawer opened; when it falls short the
      // backend charges the default card, so decide on a fresh balance.
      await fetchBilling(user.tcId, { force: true });
      const { overview, error } = useBillingStore.getState();
      if (error) throw error;
      const credit = Number(overview?.availableCredit || 0);
      if (credit >= overdueInvoice.amountDueCents) {
        await executeInvoicePay(client, undefined);
      } else {
        // Don't open a second concurrent crypto order — the panel surfaces any
        // pending one; the user must complete or cancel it first.
        const pending = (await client.hasPendingCryptoPayment({ userId: user.tcId } as any)) as any;
        if (pending?.hasPendingCryptoPayment) {
          showError(CHECKOUT_COPY.payment.crypto.errors.alreadyPending);
          return;
        }
        const amount = overdueCryptoTopUpCents(overdueInvoice.amountDueCents, credit) / 100;
        const resp = (await client.createCryptoPayment({
          userId: user.tcId,
          purchaseTotal: amount,
          creditPurchaseTotal: amount,
          payInvoiceId: overdueInvoice.invoiceId
        } as any)) as any;
        if (!resp?.purchaseUrl) {
          showError(CHECKOUT_COPY.payment.crypto.errors.initFailed);
          return;
        }
        window.location.assign(resp.purchaseUrl);
      }
    } catch (e: any) {
      showError(e?.details || e?.message || CHECKOUT_COPY.payment.cryptoErrorFallback);
    } finally {
      setSubmitting(false);
      actions.setSubmitting(false);
    }
  }

  async function handleCardSubmit() {
    if (!user?.tcId) return;
    if (!state.catalog && !overdueInvoice) return;
    setSubmitting(true);
    actions.setSubmitting(true);
    // PM id from a freshly entered card (confirmSetup). Passed explicitly when
    // confirming the subscription's PaymentIntent below.
    let newCardPmId: string | undefined;
    try {
      const client = await getBillingClient();

      if (overdueInvoice) {
        // Invoice-pay card rail: promote saved card to default (or attach a new
        // one and make it the default), then settle the invoice via the renew RPC (handles 3DS).
        if (!(await overdueInvoiceStillPayable())) return;
        let pmId: string | undefined;
        if (usingSaved) {
          const currentDefaultId = cards.find((c) => c.isDefault?.value)?.id || null;
          if (selectedCardId && selectedCardId !== currentDefaultId) {
            await client.setDefaultPaymentMethod({ userId: user.tcId, cardId: selectedCardId } as any);
          }
          pmId = cards.find((c) => c.id === selectedCardId)?.paymentMethodId;
        } else {
          pmId = (await stripeRef.current?.confirmCard()) ?? undefined;
          if (!pmId) return;
          await makeEnteredCardDefault(client, pmId);
        }
        await executeInvoicePay(client, pmId);
        return;
      }

      if (usingSaved) {
        // Charge the chosen saved card. The backend charge path bills the
        // account's default PM, so promote the selection to default first when
        // it isn't already. No confirmSetup — the card is already on file.
        const currentDefaultId = cards.find((c) => c.isDefault?.value)?.id || null;
        if (selectedCardId && selectedCardId !== currentDefaultId) {
          await client.setDefaultPaymentMethod({ userId: user.tcId, cardId: selectedCardId } as any);
        }
      } else if (!creditCoversDue || cardEntered) {
        // New card: confirmSetup attaches the PM to the customer and returns its
        // Stripe id. A first subscription is created default_incomplete; on a
        // brand-new account the customer has no default PM, so the first-invoice
        // PaymentIntent lands with no payment method. We therefore hand this PM
        // straight to confirmCardPayment below — without it confirm fails with
        // "PaymentIntent does not have a payment method". It also becomes the
        // default (by its Stripe id: its card row lands later via webhook), so a
        // plan change, which bills the default, charges this card and not the old one.
        //
        // Skipped only when credit covers a $0 due and the form is untouched —
        // the card is optional there and checkout settles off the balance.
        const pmId = await stripeRef.current?.confirmCard();
        if (!pmId) return;
        newCardPmId = pmId;
        await makeEnteredCardDefault(client, pmId);
      }

      await executeCheckout(client, newCardPmId, usingSaved);
    } catch (e: any) {
      const msg = e?.details || e?.message || CHECKOUT_COPY.payment.errorFallback;
      showError(msg);
    } finally {
      setSubmitting(false);
      actions.setSubmitting(false);
    }
  }

  // Settle a $0 due straight off the account balance — no card token, no crypto
  // redirect. Stripe finalizes the first invoice against the customer balance,
  // so executeCheckout gets no client secret and completes with no confirm step.
  async function payViaCredit() {
    if (!user?.tcId || !state.catalog) return;
    setSubmitting(true);
    actions.setSubmitting(true);
    try {
      await executeCheckout(await getBillingClient(), undefined, false);
    } catch (e: any) {
      showError(e?.details || e?.message || CHECKOUT_COPY.payment.errorFallback);
    } finally {
      setSubmitting(false);
      actions.setSubmitting(false);
    }
  }

  async function handleCryptoSubmit() {
    // Credit covers the due → the crypto provider has nothing to settle (it
    // rejects $0). Honour the buyer's crypto pick but complete off the balance
    // instead of redirecting to the hosted-payment page.
    if (creditCoversDue) return payViaCredit();
    setSubmitting(true);
    try {
      await cryptoRef.current?.submit();
    } catch (e: any) {
      showError(e?.details || e?.message || CHECKOUT_COPY.payment.cryptoErrorFallback);
    } finally {
      setSubmitting(false);
    }
  }

  // With a saved card selected, allow submit even before form is touched
  // (saved-card path). Otherwise require the Stripe new-card form to be ready.
  const cardReady = (usingSaved && !cardsLoading) || stripeReady;

  // Crypto path: button always clickable. CryptoElement.submit fetches a
  // fresh preview at click time, so race with the debounced hook is moot.
  const canSubmit = tab === PAYMENT_TAB.CARD ? cardReady && !submitting : !submitting;

  // Invoice-pay crypto CTA: instant-from-balance when credit covers, else a
  // crypto top-up of the exact amount. Null on the card tab / non-invoice mode.
  const cryptoInvoiceCta =
    overdueInvoice && tab === PAYMENT_TAB.CRYPTO
      ? availableCredit >= overdueInvoice.amountDueCents
        ? CHECKOUT_COPY.payment.overdue.payFromBalanceCta(formatCents(overdueInvoice.amountDueCents, overdueInvoice.currency))
        : CHECKOUT_COPY.payment.overdue.payWithCryptoCta(
            formatCents(overdueCryptoTopUpCents(overdueInvoice.amountDueCents, availableCredit), overdueInvoice.currency)
          )
      : null;

  return (
    <Stack spacing={2.5}>
      {/* A room is configured in the Rooms modal, and standalone EA Hosting in
          the EA Connect modal — neither has a cart to go back to. */}
      {!state.signalManagerSelected && !state.eaHostingStandalone && (
        <Button
          variant="text"
          size="small"
          startIcon={<ArrowLeftOutlined />}
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => actions.goToConfigure()}
        >
          {COPY.back}
        </Button>
      )}

      <StepHeader copy={COPY} />

      {paymentTabs.length > 1 && (
        <PillTabs<PaymentTab> options={paymentTabs} value={tab} onChange={setTab} ariaLabel={CHECKOUT_COPY.payment.tabs.ariaLabel} />
      )}

      <Box sx={{ display: tab === PAYMENT_TAB.CARD ? 'block' : 'none' }}>
        {cardsLoading ? (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              {CHECKOUT_COPY.payment.card.loading}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1.5} data-tour={TOUR_ANCHOR.CHECKOUT_CARD}>
            {cards.length > 0 && (
              <SavedCardsList
                cards={cards}
                selectedId={selectedCardId}
                useNewCard={useNewCard}
                onSelectCard={(id) => {
                  setSelectedCardId(id);
                  setUseNewCard(false);
                }}
                onUseNewCard={() => setUseNewCard(true)}
              />
            )}
            {useNewCard && (
              <StripeCardElement ref={stripeRef} onReady={() => setStripeReady(true)} onError={showError} onChange={setCardEntered} />
            )}
          </Stack>
        )}
      </Box>
      <Box sx={{ display: tab === PAYMENT_TAB.CRYPTO ? 'block' : 'none' }}>
        {overdueInvoice ? <OverdueCryptoPanel /> : <CryptoElement ref={cryptoRef} onError={showError} />}
      </Box>

      {overdueInvoice ? <OverdueAmountSummary /> : <TotalSummary />}

      <Typography variant="caption" color="text.secondary">
        {tab === PAYMENT_TAB.CRYPTO ? (
          COPY.crypto.disclaimer
        ) : (
          <>
            {COPY.disclaimerPre}
            <Box component="strong" sx={{ color: 'text.primary' }}>
              {COPY.disclaimerCompany}
            </Box>
            {COPY.disclaimerPost}
          </>
        )}
      </Typography>

      <Button
        data-tour={TOUR_ANCHOR.CHECKOUT_PAY}
        variant="contained"
        size="large"
        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <LockOutlined />}
        disabled={!canSubmit}
        onClick={tab === PAYMENT_TAB.CARD ? handleCardSubmit : overdueInvoice ? handleCryptoInvoiceSubmit : handleCryptoSubmit}
        sx={(theme) => inkButtonSx(theme.palette.mode === 'dark')}
      >
        {submitting ? COPY.ctaProcessing : cryptoInvoiceCta || COPY.ctaPayPrefix}
      </Button>
    </Stack>
  );
}
